import React from "react";
import { Field } from "redux-form";

import { ReduxControls } from "rhombus-react";

import { formatBytes, noop } from "utils/common";
import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import {
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString, isRequiredAssetField,
	nameWithPlacementPrefix,
	nonDynamicFields
} from "social/utils";

import { FACEBOOK_IMAGE_CONSTRAINTS } from "social/validators/facebook";
import FacebookDisclaimer from "social/FacebookDisclaimer";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import { useSocialFields } from "social/formats/utils";
import { FB_CTA_OPTIONS } from "social/constants/CTA";
import usePlacementDynamicUpdate from "hooks/usePlacementDynamicUpdate";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

const IMAGE_ACCEPT = ".jpg, .jpeg, .png, .gif";
const { MAX_IMAGE_BYTES } = FACEBOOK_IMAGE_CONSTRAINTS;

const FILE_SIZE_REQUIREMENT = { name: "File Size:", description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum` };
const FILE_TYPE_REQUIREMENT = { name: "File Types:", description: IMAGE_ACCEPT };

export default function FacebookImageMODefault(props){
	const {
		libraryId,
		navigationActions,
		creativeAssetType,
		adType,
	} = props;

	const onChangeDefaultIsDynamic = usePlacementDynamicUpdate(SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO);

	const baseFields = {
		isDynamic,
		assetType,
		imposiumProject,
		imposiumComposition: imposiumCompositionWithoutProjectName,
		image: {
			name: "image",
			label: "Image",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: DynamicAssetSelect,
			defaultDynVarName: nameWithPlacementPrefix("product"),
			requirements: [
				FILE_SIZE_REQUIREMENT,
				FILE_TYPE_REQUIREMENT,
				{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1080 or larger recommended" }
			],
			creativeAssetType,
			required: isRequiredAssetField(creativeAssetType),
			accept: IMAGE_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 600x600 for better placement compatibility",
			},
			libraryId,
			navigationActions,
			onChange: onChangeDefaultIsDynamic,
		},
		name: {
			name: "name",
			label: "Creative Name",
			className: "full-width",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
			required: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			className: "full-width",
			placeholder: "Enter a short headline",
			tip: {
				description: "May get truncated after 40 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("headline"),
		},
		primaryText: {
			name: "primaryText",
			label: "Primary Text",
			className: "full-width",
			placeholder: "Tell people what your creative is about",
			tip: {
				description: "May get truncated after 125 characters",
				direction: "right",
			},
			component: DynamicInput,
			type: "textarea",
			defaultDynVarName: nameWithPlacementPrefix("copy"),
		},
		description: {
			name: "description",
			label: "Description",
			className: "full-width",
			placeholder: "Include additional details",
			tip: {
				description: "May get truncated after 30 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("description"),
		},
		disclaimer: {
			name: "disclaimerEnabled",
			component: FacebookDisclaimer,
			adType,
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			className: "full-width",
			placeholder: "https://www.example.com",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("clickTag1"),
			required: true,
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("display_link"),
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Enter Call to Action Text",
			component: DynamicSelect,
			defaultDynVarName: nameWithPlacementPrefix("cta"),
			options: FB_CTA_OPTIONS,
			normalize: dynamicNormalizeOptionToString,
			format: dynamicMapStringToOption,
			isClearable: false,
			menuPlacement: "top",
			required: true,
			onMenuScrollToBottom: noop,
		},
		sponsorPageId: {
			name: "sponsorPageId",
			label: "Sponsor Page ID",
			className: "full-width",
			placeholder: "Enter Sponsor Facebook Page ID",
			component: ReduxControls.Input,
			...nonDynamicFields(),
		},
	};

	const fieldProps = useSocialFields(baseFields, props);

	return (
		<div className="fields">
			{fieldProps.map((properties) => {
				return <Field key={properties.name} {...properties} />;
			})}
		</div>
	);
}

FacebookImageMODefault.displayName = "FacebookImageMODefaultPlacement";
